import { useEffect, useRef, useState } from 'react';
import { useToast } from '../components/Toast';
import { useModal } from '../components/ModalProvider';
import { fmtDate } from '../utils/format';
import { custAnnOptions, custAnnList, custAnnPreview, custAnnRecipients, custAnnCreate } from '../api/announcements';
import { searchCustomers } from '../api/customers';

// Modal xem danh sách KH sẽ nhận + bỏ bớt vài khách (loại trừ).
function RecipientsModal({ scope, excluded, setExcluded, onClose }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => { custAnnRecipients(scope).then(setData).catch(() => setData({ total: 0, list: [] })); }, []); // eslint-disable-line
  const ex = new Set(excluded);
  const toggle = (code) => setExcluded((arr) => (arr.includes(code) ? arr.filter((c) => c !== code) : [...arr, code]));
  const list = (data?.list || []).filter((c) => !q.trim() || `${c.code} ${c.name} ${c.sale}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div>
      <h3>Danh sách khách hàng sẽ nhận</h3>
      {!data ? <div className="empty">Đang tải…</div> : (
        <>
          <div className="small muted" style={{ marginBottom: 8 }}>
            Bỏ tick để KHÔNG gửi cho khách đó. Sẽ gửi <b style={{ color: 'var(--accent)' }}>{data.list.length - excluded.filter((c) => data.list.some((x) => x.code === c)).length}</b>/{data.total}
            {data.total > data.list.length ? ` (hiển thị ${data.list.length} đầu)` : ''}.
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm trong danh sách…" style={{ marginBottom: 8 }} />
          <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            <div className="tbl-wrap">
            <table>
              <thead><tr><th style={{ width: 40 }}>Gửi</th><th>Mã</th><th>Tên KH</th><th>Sale</th></tr></thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.code} style={ex.has(c.code) ? { opacity: 0.45 } : undefined}>
                    <td className="c"><input type="checkbox" checked={!ex.has(c.code)} onChange={() => toggle(c.code)} /></td>
                    <td className="mono">{c.code}</td>
                    <td>{c.name || '—'}</td>
                    <td className="small">{c.sale || '—'}</td>
                  </tr>
                ))}
                {!list.length && <tr><td colSpan={4} className="empty">Không có khách phù hợp</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
        </>
      )}
      <div className="row" style={{ justifyContent: 'space-between', marginTop: 14 }}>
        <button className="btn ghost sm" onClick={() => setExcluded([])}>Chọn lại tất cả</button>
        <button className="btn" onClick={onClose}>Xong</button>
      </div>
    </div>
  );
}

function fmtDateTime(d) {
  const dt = new Date(d);
  return `${fmtDate(dt)} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
}

const SCOPES = [
  ['all', 'Toàn công ty'],
  ['sale', 'Theo sale'],
  ['group', 'Theo nhóm KD'],
  ['custom', 'Chọn KH cụ thể'],
];

export default function CustomerAnnounce() {
  const toast = useToast();
  const { openModal, closeModal } = useModal();
  const [opts, setOpts] = useState({ sales: [], groups: [], year: new Date().getFullYear() });
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState('all');
  const [sales, setSales] = useState([]); // userId[]
  const [groups, setGroups] = useState([]); // groupId[]
  const [custs, setCusts] = useState([]); // {code, name}[]
  const [excluded, setExcluded] = useState([]); // custCode[] bỏ bớt
  const [count, setCount] = useState(null);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const [q, setQ] = useState('');
  const [sug, setSug] = useState([]);

  const loadHistory = () => custAnnList().then(setHistory).catch(() => {});
  useEffect(() => { custAnnOptions().then(setOpts).catch(() => {}); loadHistory(); }, []);

  // Gợi ý KH khi gõ (cho "Chọn KH cụ thể").
  const qTimer = useRef(null);
  useEffect(() => {
    if (type !== 'custom' || !q.trim()) { setSug([]); return; }
    clearTimeout(qTimer.current);
    qTimer.current = setTimeout(() => { searchCustomers(q.trim()).then(setSug).catch(() => setSug([])); }, 300);
  }, [q, type]);

  const scopeOf = () => ({
    type,
    year: opts.year,
    sales: type === 'sale' ? sales : [],
    groups: type === 'group' ? groups : [],
    custCodes: type === 'custom' ? custs.map((c) => c.code) : [],
    exclude: excluded,
  });

  // Đổi bộ lọc gốc -> xoá danh sách loại trừ (danh sách người nhận đã khác).
  useEffect(() => { setExcluded([]); }, [type, sales, groups, custs]);

  // Đếm số KH nhận (debounce) mỗi khi đổi bộ lọc / loại trừ.
  const pvTimer = useRef(null);
  useEffect(() => {
    setCount(null);
    clearTimeout(pvTimer.current);
    pvTimer.current = setTimeout(() => {
      custAnnPreview(scopeOf()).then((r) => setCount(r.count)).catch(() => setCount(null));
    }, 400);
    // eslint-disable-next-line
  }, [type, sales, groups, custs, excluded, opts.year]);

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const addCust = (c) => { if (!custs.some((x) => x.code === c.code)) setCusts([...custs, { code: c.code, name: c.name }]); setQ(''); setSug([]); };

  async function send() {
    if (sending) return;
    if (!content.trim()) return toast('Nhập nội dung thông báo', 'warn');
    if (type === 'sale' && !sales.length) return toast('Chọn ít nhất 1 sale', 'warn');
    if (type === 'group' && !groups.length) return toast('Chọn ít nhất 1 nhóm', 'warn');
    if (type === 'custom' && !custs.length) return toast('Chọn ít nhất 1 khách hàng', 'warn');
    if (!count) return toast('Không có khách hàng nào khớp bộ lọc', 'warn');
    setSending(true);
    try {
      await custAnnCreate({ title: title.trim(), content: content.trim(), scope: scopeOf() });
      toast(`Đang gửi thông báo tới ${count} khách hàng…`);
      setTitle(''); setContent('');
      setTimeout(loadHistory, 500);
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    } finally { setSending(false); }
  }

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>📣 Thông báo gửi khách hàng</h3>
      <div className="small muted" style={{ marginBottom: 14 }}>
        Gửi vào hộp tin nhắn của khách hàng (kênh CSKH). KH thấy nhãn CSKH, không thấy tên thật.
      </div>

      <div className="field">
        <label>Tiêu đề (tuỳ chọn)</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="VD: Thông báo lịch nghỉ lễ" />
      </div>
      <div className="field">
        <label>Nội dung gửi khách hàng</label>
        <textarea rows={3} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Nội dung thông báo gửi tới khách hàng…" />
      </div>

      <div className="field">
        <label>Gửi tới</label>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {SCOPES.map(([k, l]) => (
            <button type="button" key={k} className={`filterBtn${type === k ? ' on' : ''}`} onClick={() => setType(k)}>{l}</button>
          ))}
        </div>
      </div>

      {type === 'sale' && (
        <div className="field">
          <label>Chọn sale ({sales.length})</label>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {opts.sales.map((s) => (
              <button type="button" key={s.id} className={`filterBtn${sales.includes(s.id) ? ' on' : ''}`} onClick={() => toggle(sales, setSales, s.id)}>
                {s.name}{s.group ? ` · ${s.group}` : ''}
              </button>
            ))}
            {!opts.sales.length && <span className="small muted">Không có sale nào</span>}
          </div>
        </div>
      )}

      {type === 'group' && (
        <div className="field">
          <label>Chọn nhóm kinh doanh ({groups.length})</label>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {opts.groups.map((g) => (
              <button type="button" key={g.id} className={`filterBtn${groups.includes(g.id) ? ' on' : ''}`} onClick={() => toggle(groups, setGroups, g.id)}>{g.name}</button>
            ))}
            {!opts.groups.length && <span className="small muted">Không có nhóm nào</span>}
          </div>
        </div>
      )}

      {type === 'custom' && (
        <div className="field">
          <label>Chọn khách hàng ({custs.length})</label>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginBottom: custs.length ? 8 : 0 }}>
            {custs.map((c) => (
              <span key={c.code} className="tag" style={{ cursor: 'pointer' }} onClick={() => setCusts(custs.filter((x) => x.code !== c.code))} title="Bỏ chọn">
                {c.code} · {c.name} ✕
              </span>
            ))}
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm theo mã / tên KH…" />
          {sug.length > 0 && (
            <div className="card" style={{ padding: 4, marginTop: 4, maxHeight: 180, overflowY: 'auto' }}>
              {sug.map((c) => (
                <div key={c.id} className="notif-row" style={{ cursor: 'pointer', padding: '6px 8px' }} onClick={() => addCust(c)}>
                  <div style={{ flex: 1 }}><b>{c.code}</b> · {c.name} {c.sale ? <span className="small muted">— {c.sale}</span> : null}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="row between" style={{ marginTop: 8, flexWrap: 'wrap', gap: 8 }}>
        <span className="small" style={{ color: 'var(--accent)' }}>
          <b>{count == null ? '…' : count.toLocaleString('vi-VN')}</b> khách hàng sẽ nhận
          {type === 'all' && <span className="muted"> (có doanh thu năm {opts.year})</span>}
          {excluded.length > 0 && <span className="muted"> · đã bỏ {excluded.length}</span>}
        </span>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn ghost" onClick={() => openModal(<RecipientsModal scope={scopeOf()} excluded={excluded} setExcluded={setExcluded} onClose={closeModal} />, { wide: true })} disabled={!count && !excluded.length}>
            Xem / bỏ bớt danh sách
          </button>
          <button className="btn" onClick={send} disabled={sending || !count}>{sending ? 'Đang gửi…' : '📤 Gửi thông báo'}</button>
        </div>
      </div>

      <h4 style={{ marginBottom: 8, marginTop: 22 }}>Đã gửi gần đây</h4>
      {!history.length ? <div className="empty">Chưa có thông báo nào</div> : history.map((a) => {
        const s = a.stats || {};
        return (
          <div key={a._id} className="notif-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>{a.title || a.content}</div>
              {a.title && <div className="small muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.content}</div>}
              <div className="small muted mono" style={{ marginTop: 2 }}>
                {scopeLabel(a.scope)} · {a.createdByName || '—'} · {fmtDateTime(a.createdAt)}
              </div>
            </div>
            <span className="mono small" style={{ flex: '0 0 auto', color: s.done ? 'var(--ok)' : 'var(--warn)' }}>
              {s.done ? '✓ ' : '⏳ '}{s.sent || 0}/{s.total || 0}{s.failed ? ` · lỗi ${s.failed}` : ''}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function scopeLabel(sc) {
  if (!sc) return 'Toàn công ty';
  if (sc.type === 'sale') return `${(sc.sales || []).length} sale`;
  if (sc.type === 'group') return `${(sc.groups || []).length} nhóm KD`;
  if (sc.type === 'custom') return `${(sc.custCodes || []).length} KH`;
  return `Toàn công ty (DT ${sc.year || ''})`;
}
